import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {TriangleDownIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {lazy, Suspense, useState} from 'react'

import {CoreSsoBanner} from './components/CoreSsoBanner'
import type {SsoOrganization} from './types'

const SuspendingLazyBanner = lazy(async () => {
  const module = await import('./GlobalSsoBanner')
  return {default: module.GlobalSsoBanner}
})

interface LazyGlobalSsoBannerProps {
  ssoOrgs?: SsoOrganization[]
}

/**
 * Lazily rendered SSO banner which avoids importing the full banner component unless it will actually be rendered.
 * Displays a high-fidelity fallback while loading to hide the laziness from the user. Hides itself automatically if
 * lazy-loading fails. Provides its own Suspense boundary so that parent components will not be suspended.
 */
export function LazyGlobalSsoBanner({ssoOrgs}: LazyGlobalSsoBannerProps) {
  /** Will be true if the user clicks dismiss in the suspense fallback before loading completes. */
  const [dismissedBeforeLoad, setDismissedBeforeLoad] = useState(false)

  if (!ssoOrgs || ssoOrgs.length === 0 || dismissedBeforeLoad) return null

  return (
    <ErrorBoundary fallback={null}>
      <Suspense
        fallback={
          <CoreSsoBanner
            ssoOrgs={ssoOrgs}
            onDismiss={() => setDismissedBeforeLoad(true)}
            primaryAction={
              ssoOrgs.length === 1 ? (
                <Button disabled>Single sign-on</Button>
              ) : (
                // mimic ActionMenu anchor for multiple orgs
                <Button disabled size="small" trailingVisual={TriangleDownIcon}>
                  Single sign-on
                </Button>
              )
            }
          />
        }
      >
        <SuspendingLazyBanner ssoOrgs={ssoOrgs} />
      </Suspense>
    </ErrorBoundary>
  )
}
